import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, FileText, RefreshCw, QrCode } from "lucide-react";
import { formatET, todayET } from "@/lib/dates";
import { useNavigate } from "react-router-dom";

const TYPE_BADGE = {
  organization: { label: "Organization", cls: "bg-purple-500/15 text-purple-600" },
  self: { label: "Self", cls: "bg-blue-500/15 text-blue-600" },
  child: { label: "For Child", cls: "bg-amber-500/15 text-amber-600" },
};

const KIND_TABS = [
  { id: "all",         label: "All",         icon: "📋" },
  { id: "turf_house",  label: "Turf House",  icon: "🟢" },
  { id: "rrdc_field",  label: "RRDC Field",  icon: "🔵" },
];

const KIND_BADGE = {
  turf_house: { label: "Turf House", cls: "bg-emerald-500/15 text-emerald-700" },
  rrdc_field: { label: "RRDC Field", cls: "bg-sky-500/15 text-sky-700" },
};

export default function SignedWaiversPage() {
  const navigate = useNavigate();
  const [date, setDate] = useState(todayET());
  const [search, setSearch] = useState("");
  const [kindTab, setKindTab] = useState("all");
  const [waivers, setWaivers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const parts = [];
      if (search.trim()) {
        parts.push(`q=${encodeURIComponent(search.trim())}`);
      } else {
        parts.push(`date=${date}`);
      }
      if (kindTab !== "all") parts.push(`kind=${kindTab}`);
      const { data } = await api.get(`/signed-waivers?${parts.join("&")}`);
      setWaivers(data.waivers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load waivers");
    } finally {
      setLoading(false);
    }
  };

  // Reload when date/kind changes (only if no active search) and debounce search.
  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [date, search, kindTab]);

  const downloadPDF = async (w) => {
    try {
      const resp = await api.get(`/signed-waivers/${w.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `waiver_${(w.signer_name || "signer").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get(`/signed-waivers/emails.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "waiver_emails.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export CSV");
    }
  };

  return (
    <div className="space-y-4 max-w-6xl" data-testid="signed-waivers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-bold">Signed Waivers</h1>
          <p className="text-muted-foreground text-sm">Walk-in waivers signed at the public <code className="text-xs">/waiver</code> page.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/signed-waivers/qr")} data-testid="qr-poster-btn">
            <QrCode size={14} /> QR Poster
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-emails-csv">
            <Download size={14} /> Export Emails CSV
          </Button>
        </div>
      </div>

      {/* Kind tabs — Turf House vs RRDC Field waivers */}
      <div className="flex gap-2 border-b border-border pb-0" data-testid="waiver-kind-tabs">
        {KIND_TABS.map((t) => {
          const active = kindTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setKindTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`waiver-tab-${t.id}`}
            >
              <span className="mr-1.5">{t.icon}</span>{t.label}
            </button>
          );
        })}
      </div>

      <Card className="border-border">
        <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base font-outfit">
            {search.trim() ? "Search results" : "Filter by date"}
          </CardTitle>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Input
              type="search"
              placeholder="Search name, phone, organization…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-full sm:w-64 text-sm"
              data-testid="waiver-search-input"
            />
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={!!search.trim()}
              className="h-8 w-40 text-sm disabled:opacity-50"
              data-testid="waiver-date-picker"
            />
            <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="waiver-refresh">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading...</div>
          ) : waivers.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="no-waivers-msg">
              {search.trim()
                ? `No waivers match "${search.trim()}".`
                : `No waivers signed on ${date}.`}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5">Signed At</th>
                    <th className="px-4 py-2.5">Waiver</th>
                    <th className="px-4 py-2.5">Signer</th>
                    <th className="px-4 py-2.5">Type</th>
                    <th className="px-4 py-2.5">Organization</th>
                    <th className="px-4 py-2.5">Child</th>
                    <th className="px-4 py-2.5">Email</th>
                    <th className="px-4 py-2.5">Phone</th>
                    <th className="px-4 py-2.5 text-right">Document</th>
                  </tr>
                </thead>
                <tbody>
                  {waivers.map((w) => {
                    const t = TYPE_BADGE[w.signer_type] || { label: w.signer_type, cls: "bg-gray-200 text-gray-700" };
                    // Legacy waivers signed before the two-flavour split have no `kind`
                    // — they were all Turf House.
                    const kb = KIND_BADGE[w.kind || "turf_house"];
                    const child = w.signer_type === "child" ? `${w.child_first_name || ""} ${w.child_last_name || ""}`.trim() : "";
                    return (
                      <tr key={w.id} className="border-t border-border" data-testid={`waiver-row-${w.id}`}>
                        <td className="px-4 py-2.5 whitespace-nowrap text-xs text-muted-foreground">{formatET(w.signed_at)}</td>
                        <td className="px-4 py-2.5">
                          <Badge className={`${kb.cls} font-medium`}>{kb.label}</Badge>
                        </td>
                        <td className="px-4 py-2.5 font-medium">{w.signer_name || "—"}</td>
                        <td className="px-4 py-2.5">
                          <Badge className={`${t.cls} font-medium`}>{t.label}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">{w.organization || "—"}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{child || "—"}</td>
                        <td className="px-4 py-2.5"><a href={`mailto:${w.email}`} className="text-primary hover:underline">{w.email}</a></td>
                        <td className="px-4 py-2.5 text-muted-foreground">{w.phone || "—"}</td>
                        <td className="px-4 py-2.5 text-right">
                          <Button variant="ghost" size="sm" onClick={() => downloadPDF(w)} data-testid={`view-pdf-${w.id}`}>
                            <FileText size={14} /> PDF
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="px-4 py-2 text-xs text-muted-foreground border-t border-border bg-muted/20">
                {waivers.length} waiver{waivers.length === 1 ? "" : "s"} {search.trim() ? `matching "${search.trim()}"` : `on ${date}`}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
